<template>
  <div
    class="time-picker-shell"
    :class="{
      'no-ampm': !showAmpm,
      'unreviewed-tint': !modified,
      'is-continued': continued
    }"
    :style="{ '--modified-color': resolvedModifiedColor, '--continued-color': resolvedContinuedColor }"
  >
    <div class="time-picker-main">
      <v-text-field
        v-model="localTime.hours"
        type="number"
        variant="plain"
        hide-details
        density="compact"
        class="time-part hour-part"
        @change="validateHours"
        @wheel.prevent="scrollHours($event)"
      ></v-text-field>

      <span class="time-separator">:</span>

      <v-text-field
        v-model="formattedMinutes"
        type="number"
        variant="plain"
        hide-details
        density="compact"
        class="time-part minute-part"
        @change="manualMinuteChange"
        @wheel.prevent="scrollMinutes($event)"
      ></v-text-field>

      <button
        v-if="showAmpm && showContinue"
        type="button"
        class="continue-toggle"
        :class="{ active: continued }"
        title="Continued from previous shift"
        @click.stop="$emit('update:continued', !continued)"
      >
        <v-icon size="28">mdi-arrow-right-thin-circle-outline</v-icon>
      </button>
    </div>

    <v-btn
      v-if="showAmpm"
      @click="toggleAMPM"
      variant="text"
      class="ampm-toggle"
    >
      {{ localTime.ampm }}
    </v-btn>
  </div>
</template>
  
  <script>
export default {
  props: {
    modelValue: {
      type: [Object, String],
      default: null
    },
    showAmpm: {
      type: Boolean,
      default: true
    },
    modified: {
      type: Boolean,
      default: false
    },
    modifiedColor: {
      type: String,
      default: ''
    },
    showContinue: {
      type: Boolean,
      default: true
    },
    continued: {
      type: Boolean,
      default: false
    },
    continuedColor: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      localTime: this.parseModelValue(this.modelValue)
    };
  },
  watch: {
    modelValue: {
      deep: true,
      handler(newVal) {
        this.localTime = this.parseModelValue(newVal);
      }
    },
    localTime: {
      deep: true,
      handler(newVal) {
        const nextValue = this.showAmpm
          ? { ...newVal }
          : this.formatDurationValue(newVal);

        if (JSON.stringify(nextValue) !== JSON.stringify(this.modelValue)) {
          this.$emit('update:modelValue', nextValue);
        }
      }
    }
  },
  computed: {
    resolvedModifiedColor() {
      return this.modifiedColor || 'rgba(76, 175, 80, 0.55)';
    },
    resolvedContinuedColor() {
      return this.continuedColor || this.modifiedColor || 'rgba(76, 175, 80, 0.85)';
    },
    formattedMinutes: {
      get() {
        return this.localTime.minutes.toString().padStart(2, "0");
      },
      set(value) {
        this.localTime.minutes = parseInt(value) || 0;
      },
    },
  },
  methods: {
    parseModelValue(value) {
      if (value && typeof value === 'object') {
        return {
          hours: Number(value.hours) || 0,
          minutes: Number(value.minutes) || 0,
          ampm: value.ampm || 'AM'
        };
      }

      if (!this.showAmpm && typeof value === 'string') {
        const [hoursRaw, minutesRaw] = String(value).split(':');
        return {
          hours: Number.isFinite(Number(hoursRaw)) ? Number(hoursRaw) : 0,
          minutes: Number.isFinite(Number(minutesRaw)) ? Number(minutesRaw) : 0,
          ampm: 'AM'
        };
      }

      return {
        hours: 0,
        minutes: 0,
        ampm: 'AM'
      };
    },
    formatDurationValue(time) {
      const hours = Number(time?.hours) || 0;
      const minutes = Number(time?.minutes) || 0;
      return `${hours}:${String(minutes).padStart(2, '0')}`;
    },
    toggleAMPM() {
      this.localTime.ampm = this.localTime.ampm === "AM" ? "PM" : "AM";
    },
    validateHours() {
      if (this.showAmpm) {
        this.localTime.hours = ((this.localTime.hours - 1 + 12) % 12) + 1;
        return;
      }

      if (this.localTime.hours < 0) this.localTime.hours = 23;
      if (this.localTime.hours > 23) this.localTime.hours = 0;
    },
    manualMinuteChange() {
      if (this.localTime.minutes < 0) this.localTime.minutes = 59;
      if (this.localTime.minutes > 59) this.localTime.minutes = 0;
    },
    incrementHours() {
      if (this.showAmpm) {
        this.localTime.hours = this.localTime.hours === 12 ? 1 : this.localTime.hours + 1;
        return;
      }
      this.localTime.hours = this.localTime.hours === 23 ? 0 : this.localTime.hours + 1;
    },
    decrementHours() {
      if (this.showAmpm) {
        this.localTime.hours = this.localTime.hours === 1 ? 12 : this.localTime.hours - 1;
        return;
      }
      this.localTime.hours = this.localTime.hours === 0 ? 23 : this.localTime.hours - 1;
    },
    incrementMinutes() {
      this.localTime.minutes = this.localTime.minutes === 59 ? 0 : this.localTime.minutes + 1;
    },
    decrementMinutes() {
      this.localTime.minutes = this.localTime.minutes === 0 ? 59 : this.localTime.minutes - 1;
    },
    scrollHours(event) {
      if (document.activeElement !== event.target) return;
      event.deltaY < 0 ? this.incrementHours() : this.decrementHours();
    },
    scrollMinutes(event) {
      if (document.activeElement !== event.target) return;
      event.deltaY < 0 ? this.incrementMinutes() : this.decrementMinutes();
    }
  }
};
  </script>

<style scoped>
.time-picker-shell {
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.26);
  border-radius: 6px;
  background: #ffffff;
  margin: 0;
}

.time-picker-shell.unreviewed-tint {
  background: color-mix(in srgb, var(--modified-color, rgba(76, 175, 80, 0.55)) 10%, white);
}

.time-picker-main {
  display: inline-flex;
  align-items: center;
  flex: 1 1 auto;
  gap: 0;
  min-width: 0;
  padding: 0 8px 0 9px;
  height: 100%;
  box-sizing: border-box;
}

.time-separator {
  font-size: 16px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.72);
  width: 12px;
  min-width: 12px;
  text-align: center;
  margin: 0 3px 0 1px;
}

.continue-toggle {
  width: 16px;
  min-width: 16px;
  height: 100%;
  border: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.42);
  opacity: 0;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  padding: 0;
  margin-right: 1px;
  margin-left: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 1px;
  transition:
    opacity 0.15s ease,
    color 0.15s ease,
    transform 0.15s ease;
}

.time-picker-shell:hover .continue-toggle,
.continue-toggle.active,
.continue-toggle:focus-visible {
  opacity: 1;
}

.continue-toggle:hover {
  transform: translateX(1px);
}

.continue-toggle.active {
  color: var(--continued-color, var(--modified-color, rgba(76, 175, 80, 0.85)));
  font-weight: 700;
}

.ampm-toggle {
  min-width: 48px !important;
  height: 100% !important;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0 !important;
  font-size: 12px !important;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.62) !important;
  padding: 0 10px !important;
  flex: 0 0 auto;
}

.time-picker-shell.no-ampm .time-picker-main {
  padding-right: 10px;
}

.time-part {
  min-width: 46px;
  width: 46px;
  flex: 0 0 46px;
  box-sizing: border-box;
}

.hour-part {
  min-width: 48px;
  width: 48px;
  flex-basis: 48px;
  margin-right: -4px;
}

.minute-part {
  min-width: 48px;
  width: 48px;
  flex-basis: 48px;
}

.time-part :deep(.v-field) {
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  min-height: 38px !important;
  height: 38px !important;
  box-sizing: border-box;
  overflow: visible !important;
}

.time-part :deep(.v-field__input) {
  min-height: 38px !important;
  padding: 0 6px !important;
  justify-content: center;
  box-sizing: border-box;
  overflow: visible !important;
}

.time-part :deep(input) {
  text-align: center;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.88);
  padding: 0 4px !important;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;
  min-width: 0;
}

.time-part :deep(.v-field__outline) {
  display: none !important;
}
</style>
